// Camada de dados pública sobre Supabase.
//
// Todas as leituras/escritas passam por RLS (ver supabase/migrations).
// O frontend usa apenas a chave publicável — nada de segredos aqui.
import { supabase, supabaseConfigMissing } from './supabaseClient';
import { notifyBrevo } from './notifyBrevo';
import { cachedQuery } from './queryCache';

const TTL_LIST = 3 * 60 * 1000;
const TTL_DETAIL = 2 * 60 * 1000;

// Gera um UUID no cliente. Permite inserir (anon) sem ler a linha de volta:
// a política de SELECT exige is_staff(), por isso um insert com .select()
// seria recusado por RLS. Geramos o id aqui e omitimos o .select() (o
// supabase-js envia return=minimal quando não há .select()).
function newId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const JOB_LIST_COLUMNS =
  'id, title, slug, description, location, job_type, experience_level, salary, is_remote, published_at';

// ---------------------------------------------------------------------------
// Serviços
// ---------------------------------------------------------------------------
export async function getServices() {
  return cachedQuery('services:active', TTL_LIST, async () => {
    const { data, error } = await supabase
      .from('services')
      .select('id, name, slug, description, icon, image_url, sort_order')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return (data || []).map((s) => ({
      ...s,
      title: s.name,
    }));
  });
}

// ---------------------------------------------------------------------------
// Vagas (recrutamento)
// ---------------------------------------------------------------------------
export async function getJobs() {
  if (supabaseConfigMissing) return [];

  return cachedQuery('jobs:active', TTL_LIST, async () => {
    const { data, error } = await supabase
      .from('job_posts')
      .select(JOB_LIST_COLUMNS)
      .eq('is_active', true)
      .order('published_at', { ascending: false, nullsFirst: false });

    if (error) throw error;
    return data || [];
  });
}

export async function getJobBySlug(slug) {
  const { data, error } = await supabase
    .from('job_posts')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function submitApplication(application) {
  // Mapeia o formulário (camelCase) para as colunas da tabela.
  const row = {
    job_post_id: application.jobPostId || application.job_post_id || null,
    nome: application.nome || application.name,
    email: application.email,
    telefone: application.telefone || application.phone,
    localizacao: application.localizacao,
    cargo_atual: application.cargoAtual,
    experiencia: application.experiencia,
    formacao: application.formacao,
    salario: application.salario,
    competencias: application.competencias,
    motivacao: application.motivacao,
    disponibilidade: application.disponibilidade,
    linkedin: application.linkedin,
    portfolio: application.portfolio,
    documentos: application.documentos,
    informacoes: application.informacoes,
  };

  const id = newId();
  const { error } = await supabase
    .from('job_applications')
    .insert({ id, ...row }, { returning: 'minimal' });

  if (error) throw error;
  void notifyBrevo('job_application', id);
  return { success: true, id };
}

// ---------------------------------------------------------------------------
// Contacto
// ---------------------------------------------------------------------------
export async function submitContact(contact) {
  const row = {
    name: contact.name,
    email: contact.email,
    phone: contact.phone || null,
    subject: contact.subject,
    message: contact.message,
    source: contact.source || 'website',
  };

  const id = newId();
  const { error } = await supabase
    .from('contact_messages')
    .insert({ id, ...row }, { returning: 'minimal' });

  if (error) throw error;
  void notifyBrevo('contact', id);
  return { success: true, id };
}

// ---------------------------------------------------------------------------
// Newsletter
// ---------------------------------------------------------------------------
export async function subscribeNewsletter(email, name = null, gender = null) {
  if (supabaseConfigMissing) {
    throw new Error('Supabase não configurado');
  }

  const id = newId();
  const row = {
    id,
    email,
    name: name?.trim() || null,
    gender: gender === 'male' || gender === 'female' ? gender : null,
    is_active: true,
  };
  const { error } = await supabase.from('newsletter_subscribers').insert(row);

  // 23505 = unique_violation (já subscrito) — tratado como sucesso idempotente.
  if (error) {
    if (error.code === '23505' || error.message?.includes('duplicate key')) {
      return { success: true, alreadySubscribed: true };
    }
    throw error;
  }

  void notifyBrevo('newsletter', id);
  return { success: true, id };
}

// ---------------------------------------------------------------------------
// Blog
// ---------------------------------------------------------------------------
const POST_BASE_COLUMNS =
  'id, title, slug, excerpt, content, cover_image, tags, featured, published, status, published_at, reading_time_minutes';

function isSchemaCompatError(error) {
  const msg = String(error?.message || error?.details || '').toLowerCase();
  return msg.includes('gallery')
    || msg.includes('post_categories')
    || msg.includes('column')
    || msg.includes('relationship')
    || msg.includes('schema cache');
}

async function queryPublishedPosts(selectVariants, { limit, featuredFirst } = {}) {
  if (supabaseConfigMissing) return [];

  for (const select of selectVariants) {
    let q = supabase
      .from('posts')
      .select(select)
      .or('published.eq.true,status.eq.published');

    if (featuredFirst) q = q.order('featured', { ascending: false });
    q = q.order('published_at', { ascending: false, nullsFirst: false });
    if (limit) q = q.limit(limit);

    const { data, error } = await q;
    if (!error) return data || [];
    if (!isSchemaCompatError(error)) throw error;
  }

  // Último recurso — colunas mínimas (compatível com schema antigo)
  let q = supabase
    .from('posts')
    .select('id, title, slug, excerpt, content, cover_image, tags, featured, published_at')
    .eq('published', true)
    .order('published_at', { ascending: false, nullsFirst: false });
  if (limit) q = q.limit(limit);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function getPosts() {
  return cachedQuery('posts:all', TTL_LIST, async () => {
    const data = await queryPublishedPosts([
      `${POST_BASE_COLUMNS}, gallery, post_categories ( name, slug )`,
      `${POST_BASE_COLUMNS}, post_categories ( name, slug )`,
      `${POST_BASE_COLUMNS}, gallery`,
      POST_BASE_COLUMNS,
    ]);
    return data.map(mapPublicPost);
  });
}

export async function getFeaturedPosts(limit = 6) {
  return cachedQuery(`posts:featured:${limit}`, TTL_LIST, async () => {
    const data = await queryPublishedPosts([
      `${POST_BASE_COLUMNS}, gallery, post_categories ( name )`,
      `${POST_BASE_COLUMNS}, post_categories ( name )`,
      `${POST_BASE_COLUMNS}, gallery`,
      POST_BASE_COLUMNS,
    ], { limit, featuredFirst: true });
    return data.map(mapPublicPost);
  });
}

function mapPublicPost(row) {
  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt,
    content: row.content,
    cover_image: row.cover_image,
    coverImage: row.cover_image,
    gallery: row.gallery || [],
    tags: row.tags || [],
    featured: row.featured,
    publishedAt: row.published_at,
    published_at: row.published_at,
    readTime: row.reading_time_minutes || 3,
    reading_time_minutes: row.reading_time_minutes,
    category: row.post_categories?.name || 'Geral',
    post_categories: row.post_categories,
    author: { name: 'SOSVOR' },
  };
}

export async function getPostBySlug(slug) {
  return cachedQuery(`post:slug:${slug}`, TTL_DETAIL, async () => {
    const attempts = [
      `*, post_categories ( name, slug )`,
      '*',
    ];

    for (const select of attempts) {
      const { data, error } = await supabase
        .from('posts')
        .select(select)
        .eq('slug', slug)
        .or('published.eq.true,status.eq.published')
        .maybeSingle();

      if (!error) {
        if (!data) return null;
        return {
          ...data,
          coverImage: data.cover_image,
          publishedAt: data.published_at,
          readTime: data.reading_time_minutes || 3,
          category: data.post_categories?.name || 'Geral',
          author: 'SOSVOR',
          gallery: Array.isArray(data.gallery) ? data.gallery : [],
        };
      }
      if (!isSchemaCompatError(error)) throw error;
    }

    return null;
  });
}
